import {
    PropsWithChildren,
    createContext,
    useContext,
    useEffect,
    useState,
} from 'react';
import { Config } from '../fetchers/ConfigFetcher/useConfig';
import sentryInit from '../sentry';
import { TRACKING_SHARE_ID } from '../fetchers/SessionTracker/useTrackSession';

export type GlobalConfig = {
    accountId: string;
    apiBaseUrl: string;
    brandColor?: string;
    sentryEnvironment?: string;
    config?: Config;
};

type GlobalContextType = GlobalConfig & {
    isReady: boolean;
    setBrandColor: (brandColor: string) => void;
    setConfig: (config: Config) => void;
    setIsReady: (isReady: boolean) => void;
};

const DEFAULT_BRAND_COLOR = '#346db5';
const DEFAULT_SENTRY_ENVIRONMENT = 'local';

/**
 * The type-trickery here is intentional.  We need the context to be undefined until the global state is loaded.
 * However we also need to be able to use the context without checking for undefined everywhere always.
 * We block rendering any children until the global state is loaded, so we can safely assume that the context
 * will be defined by the time we need to use it.
 */
const GlobalContext = createContext<GlobalContextType>(
    undefined as unknown as GlobalContextType
);

export const GlobalProvider = ({
    children,
    ...props
}: PropsWithChildren<GlobalConfig>): JSX.Element | null => {
    const [brandColor, setBrandColor] = useState<string>(
        props.brandColor ?? DEFAULT_BRAND_COLOR
    );
    const [config, setConfig] = useState<Config | undefined>(props.config);
    const [isReady, setIsReady] = useState(false);
    const [sentryEnvironment] = useState(props.sentryEnvironment); // No setter, can only init sentry once

    useEffect(() => {
        const queryString = window.location.search;
        const urlParams = new URLSearchParams(queryString);
        const shareId = urlParams.get('shareId');
        if (shareId) {
            window.localStorage.setItem(TRACKING_SHARE_ID, shareId);
        }
        sentryInit(
            sentryEnvironment?.length
                ? sentryEnvironment
                : DEFAULT_SENTRY_ENVIRONMENT
        );
    }, []);

    return (
        <GlobalContext.Provider
            value={{
                accountId: props.accountId,
                apiBaseUrl: props.apiBaseUrl,
                brandColor,
                setBrandColor,
                sentryEnvironment,
                config,
                setConfig,
                isReady,
                setIsReady: (isReady: boolean) => {
                    if (isReady) {
                        console.info('RWL Portfolio initialized');
                    }
                    setIsReady(isReady);
                },
            }}
        >
            {children}
        </GlobalContext.Provider>
    );
};

export const useGlobal = () => {
    const context = useContext(GlobalContext);
    if (context === undefined) {
        throw new Error('useGlobal must be used within a GlobalProvider');
    }
    return context!;
};

export const WhenGlobalReady = ({ children }: PropsWithChildren<unknown>) => {
    const { isReady } = useGlobal();
    if (!isReady) {
        console.info('Waiting for app to initialize...');
        return null;
    }
    return <>{children}</>;
};
